import { useMemo, useState } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { patchRoutine, type WebRoutine } from '../../../../lib/routines';
import { routineGroupSections, routineGroupTitleKey } from '../../../../lib/routine-groups';
import { useRoutineCopy } from '../../routines/use-routine-copy';
import { Switch, Typography } from '../../ui';
import styles from './ConnectorRoutineRows.module.css';

/**
 * One connector's routines as switch rows, grouped by what they promise, running before paused.
 *
 * Shared by `PostConnectedConnectorCard` (one app under one reply) and `BandCard` (an app row the
 * reader expanded), because those two draw the SAME rows and a second copy of this would drift the
 * moment either card was touched — the grouping, the ordering and the toggle are all rules the phones
 * hold too.
 *
 * The sub-group headings render here and not on the Routines screen: a card holds one connector, so a
 * heading cannot repeat, while the screen nests the same handful under every connector until they stop
 * carrying information.
 */
export function ConnectorRoutineRows({
  routines,
  onToggleReported,
}: {
  routines: WebRoutine[];
  /** Called with the routine and the state it is being moved TO, for the caller's own click event.
   *  Opt-in: `PostConnectedConnectorCard` passes nothing and is byte-identical, so adding the band
   *  card's measurement cannot quietly change what the connector card reports. */
  onToggleReported?: (routine: WebRoutine, paused: boolean) => void;
}) {
  const { t } = useTranslation();
  const { scheduleText } = useRoutineCopy();
  const [inFlight, setInFlight] = useState<Record<string, boolean>>({});
  const sections = useMemo(() => routineGroupSections(routines), [routines]);

  const toggle = async (routine: WebRoutine) => {
    if (!routine.id || inFlight[routine.id]) return;
    // Reported on the INTENT, not on the response: the patch is optimistic and rolls back on failure, so
    // waiting for it would drop every flip the server refused — which is the half worth seeing.
    onToggleReported?.(routine, !routine.paused);
    setInFlight((current) => ({ ...current, [routine.id]: true }));
    // patchRoutine is optimistic and rolls back on failure, so a refused flip leaves the switch showing
    // what is really running — which is this row's whole job. No local mirror of the state to drift.
    await patchRoutine(routine, { paused: !routine.paused });
    setInFlight((current) => ({ ...current, [routine.id]: false }));
  };

  if (sections.length === 0) return null;

  return (
    <>
      {sections.map((section) => (
        <div key={section.group} className={styles.group}>
          <Typography variant="p2" className={styles.groupHeading}>
            {t(routineGroupTitleKey(section.group))}
          </Typography>
          <ul className={styles.routines}>
            {section.routines.map((routine) => (
              <li key={routine.id} className={styles.routine}>
                <Switch
                  checked={!routine.paused}
                  disabled={Boolean(routine.id && inFlight[routine.id])}
                  onChange={() => void toggle(routine)}
                  label={routine.name ?? ''}
                  description={[routine.description, scheduleText(routine)].filter(Boolean).join(' · ')}
                  labelPlacement="left"
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}
